import Phaser from 'phaser';
import * as C from 'tower/constants';
import {
  State,
  StateMachine,
  Direction,
  frameMs,
  relativeDirection,
  oppositeDirection,
  setVelocityTowards,
  wait,
  asyncAnimation,
  WaitState,
  NonceState,
} from 'tower/util';
import BattleScene from 'tower/scenes/battle';
import FlashableSprite from 'tower/flashable';
import { HitOptions } from 'tower/types';
import gameState from 'tower/state/game-state';

export interface EnemyOptions {
  hp?: number;
  touchDamage?: number;
  hideSpawn?: boolean;
  knockback?: boolean;
  passthroughWalls?: boolean;
  /** If true, small stun weapons (boomerang) will do damage. */
  stunDamage?: boolean;
  stunnable?: boolean;
  direction?: Direction;
  /** If false, the sprite will not flip based on direction. Default true. */
  turnable?: boolean;
  /** If true, the enemy is solid and immovable to the player. */
  solid?: boolean;
  passthroughDoors?: boolean;
  /** If false, the enemy will not flash when hurt. Default true. */
  damageFlash?: boolean;
}

export default abstract class Enemy extends FlashableSprite {
  stateMachine: StateMachine<State>;
  hp: number | null = null;
  touchDamage: number | null = null;
  declare body: Phaser.Physics.Arcade.Body;

  _heldItem: C.ItemKey | null = null;
  heldItemImage: Phaser.GameObjects.Image;

  protected _direction: Direction = Direction.Left;
  public invulnerable = false;
  originalState: string;

  constructor(
    public scene: BattleScene,
    x: number,
    y: number,
    texture: string,
    frame: number,
    public enemyType: string,
    public enemyOptions: EnemyOptions,
  ) {
    super(scene, x, y, texture, frame);
    this.setDepth(C.DEPTHS.Enemies);

    this.direction = Direction.Down;
    this.stateMachine = this.makeStateMachine(scene);

    // Store original state in case we replace it with the spawn state so that we can reference it later.
    this.originalState = this.stateMachine.initialState;

    // Sub in spawn state for initial state
    if (!enemyOptions.hideSpawn) {
      this.setVisible(false);
      this.stateMachine.addStates({
        spawn: new SpawnState(scene, this, this.stateMachine.initialState),
      });
      this.stateMachine.initialState = 'spawn';
    }

    // Enemies with HP can be hurt
    if (enemyOptions.hp) {
      this.hp = enemyOptions.hp;
      this.stateMachine.addStates({
        hurt: new HurtState(scene, this),
        death: new DeathState(scene, this),
      });
    }

    // Enemies with touch damage will hurt you on touch
    if (enemyOptions.touchDamage) {
      this.touchDamage = enemyOptions.touchDamage;
    }

    // Stunnable enemies are stunned by some weapons (boomerang)
    if (enemyOptions.stunnable) {
      this.stateMachine.addStates({
        stunned: new WaitState(120, this.originalState),
      });
    }

    if (enemyOptions.direction) {
      this.direction = enemyOptions.direction;
    }

    scene.add.existing(this);
    scene.physics.add.existing(this);

    if (enemyOptions.solid) {
      this.body.setImmovable(true);
    }

    this.heldItemImage = scene.add.image(x, y, 'items', 0).setVisible(false).setDepth(C.DEPTHS.Items);
  }

  abstract makeStateMachine(scene: BattleScene): StateMachine<State>;

  get direction() {
    return this._direction;
  }

  set direction(newDirection: Direction) {
    this._direction = newDirection;
    if (this.enemyOptions.turnable === false) {
      return;
    }

    if (newDirection === Direction.Left) {
      this.setFlipX(true);
    } else {
      this.setFlipX(false);
    }
  }

  async hit(options: HitOptions) {
    if (this.hp != null && !this.invulnerable) {
      // Skip damage for boomerang unless stun damage is allowed.
      if (options.weaponType !== 'boomerang' || this.enemyOptions.stunDamage) {
        this.hp -= options.damage;
      }

      if (this.hp <= 0) {
        await this.stateMachine.transition('death', options);
      } else {
        await this.stateMachine.transition('hurt', options);
      }
    }
  }

  get heldItem() {
    return this._heldItem;
  }

  set heldItem(value: C.ItemKey | null) {
    this._heldItem = value;
    if (value === null) {
      this.heldItemImage.setVisible(false);
    } else {
      this.heldItemImage.setVisible(true).setFrame(C.ITEMS[value].itemFrame);
    }
  }

  update() {
    if (this.isDestroyed) {
      return;
    }

    this.heldItemImage.setPosition(this.x, this.y);
    this.stateMachine.update();
  }

  destroy(fromScene?: boolean) {
    super.destroy(fromScene);
    this.heldItemImage.destroy(fromScene);
  }

  static preload(scene: Phaser.Scene) {
    scene.load.spritesheet('enemies', 'img/enemies.png', { frameWidth: 16, frameHeight: 16 });
    scene.load.audio('enemyHurt', 'audio/enemy_hurt.mp3');
    scene.load.audio('enemyDeath', 'audio/enemy_death.mp3');
    scene.load.audio('deflectSmall', 'audio/deflectsmall.mp3');
    scene.load.audio('deflectBig', 'audio/deflectbig.mp3');
  }

  static create(scene: BattleScene) {
    scene.anims.create({
      key: 'enemySpawn',
      duration: frameMs(24),
      frames: scene.anims.generateFrameNumbers('enemies', {
        frames: [14, 22, 30],
      }),
      repeat: 0,
    });
    scene.anims.create({
      key: 'enemyDeath',
      frameRate: 30,
      frames: scene.anims.generateFrameNumbers('enemies', {
        frames: [11, 19, 3, 26, 10, 18, 3, 27, 11],
      }),
      repeat: 0,
    });
  }
}

export class EnemyState extends NonceState {
  constructor(
    protected scene: BattleScene,
    protected enemy: Enemy,
  ) {
    super();
  }
}

export class SpawnState extends EnemyState {
  constructor(
    scene: BattleScene,
    enemy: Enemy,
    protected initialState: string,
  ) {
    super(scene, enemy);
  }

  async handleEntered() {
    this.enemy.setVisible(true);
    await asyncAnimation(this.enemy, 'enemySpawn');
    this.enemy.emit('spawncomplete');
    this.transition(this.initialState);
  }
}

class HurtState extends EnemyState {
  constructor(scene: BattleScene, enemy: Enemy) {
    super(scene, enemy);
  }

  async handleEntered({ weaponType, weaponBody, directionOverride }: HitOptions) {
    const { enemy, scene } = this;
    enemy.invulnerable = true;
    if ((enemy.enemyOptions.damageFlash ?? true) === true) {
      enemy.flash(3).on('complete', () => (enemy.invulnerable = false));
    } else {
      wait(scene, frameMs(24)).then(() => (enemy.invulnerable = false));
    }
    scene.sound.play('enemyHurt');

    if (enemy.enemyOptions.knockback ?? true) {
      const pushTowards = directionOverride ?? oppositeDirection(relativeDirection(enemy.body, weaponBody));
      setVelocityTowards(enemy.body, pushTowards, C.ENEMY_PUSH_SPEED);
      await wait(scene, frameMs(C.ENEMY_PUSH_FRAMES));
      enemy.body.setVelocity(0);
    }

    // Stun
    if (enemy.enemyOptions.stunnable) {
      // Start new stun if hit by boomerang
      if (weaponType === 'boomerang') {
        enemy.body.setVelocity(0);
        return this.transition('stunned', true);
      }

      // Return to existing stun if it was not completed
      if ((enemy.stateMachine.possibleStates.stunned as WaitState).hasRemainingDelay) {
        enemy.body.setVelocity(0);
        return this.transition('stunned');
      }
    }

    return this.transition(enemy.originalState);
  }
}

class DeathState extends EnemyState {
  handleEntered() {
    const { scene, enemy } = this;

    if (enemy.heldItem !== null) {
      const roomKey = scene.currentRoom.roomConfig.key;
      gameState.addRoomItem(roomKey, {
        id: `${enemy.heldItem}[${roomKey}]`,
        type: enemy.heldItem,
        x: enemy.x,
        y: enemy.y,
      });
    } else {
      if (Math.random() < C.HEART_DROP_CHANCE) {
        scene.currentRoom.spawnEntity({ type: 'smallHeart', x: enemy.x, y: enemy.y });
      }
    }

    const explosion = scene.add
      .sprite(enemy.x, enemy.y, 'enemies', 11)
      .setDepth(C.DEPTHS.Enemies)
      .play('enemyDeath')
      .once('animationcomplete', () => explosion.destroy());
    scene.sound.play('enemyDeath');
    this.enemy.destroy();

    scene.events.emit('enemydeath', this.enemy);
  }
}
